import React, {Component} from 'react';
import {useRouterHistory} from 'react-router';
import {createMemoryHistory} from 'history/lib';
import PropTypes from 'prop-types';
import DocsSDKWrapper from './services/docsSDKWrapper';
import {getCurrentToken} from './services/getCurrentToken';
import createStore from './app/store';
import {Provider} from 'react-redux';
import {STORE_CONTEXT} from './constants/redux';
import {
  ENV_CONFIGS,
  ENV_TYPE_BY_ISSUES_ENV_TYPE,
  initEnvConfig,
  ISSUES_ENV_TYPES,
  ISSUES_REGIONS
} from './constants/envs';
import {initialize as initializeFF} from './utils/featureFlags';
import {parseQueryString, stringifyQuery} from './utils/query-helpers';
import {
  getAnalyticsMandatoryPeopleProps,
  registerAnalyticsSuperPropsHelper,
  getAnalyticsPeopleProps,
  createAnalyticsManager
} from './utils/analytics-helpers';
import {setLanguage, setLocale} from './utils/i18n';
import JsonAPI from './services/jsonapi';
import IssuesV2Api from './services/issues-v2-api';
import {
  setFFLoaded,
  configureAnalyticsAction,
  configureJsonApiAction,
  configureJsonApiDocsSDKAction,
  setOxygenEntitiesAction,
  setCurrentProject,
  fetchCurrentUserAction,
  fetchCurrentDocsProjectAction,
  fetchIssuesCustomizationMetadata,
  fetchIssueRootCausesAction,
  setAnalyticsId,
  registerAnalyticsMandatorySuperProps,
  setAnalyticsMandatoryPeopleProps,
  setAnalyticsPeopleProps,
  setFeatureFlagAction,
  fetchIssueTemplateCategoriesAction,
  fetchActiveTemplatesAction
} from './app/actions';
import LocationsThunk from './app/thunk/locationsThunk';
import {roleType, companyType, oxygenUserType} from './constants/proptype-validation';
import {CONTAINER_TYPES} from './constants/entitlements';
import PushpinsControl from './services/lmv/pushpins-control';
import {version} from '../package.json';
import MatrixProvider from '@adsk/bim360-matrix-react-matrix-provider';
import {getToken} from './utils/getToken';
import {fetchRequiredFields} from './app/thunk/requiredfields-thunk';
import DeletedUsersProvider from './components/deleted-users/DeletedUsersProvider';

const entityPropTypes = {
  PROJECT_ENTITLEMENTS: 'currentProjectEntitlements',
  CURRENT_USER: 'currentUser',
  USERS: 'users',
  COMPANIES: 'companies',
  ROLES: 'roles'
};

class IssueProvider extends Component {
  constructor(props) {
    super(props);

    this.getState = this.getState.bind(this);
    this.dispatch = this.dispatch.bind(this);
    this.getToken = this.getToken.bind(this);

    const {locale, region, env, apigeeUrl} = props;

    // Initializing ENV_CONFIG
    initEnvConfig({region, env, apigeeUrl});
    setLanguage(locale);
    setLocale(locale);

    const analyticsManager = createAnalyticsManager(region, locale);

    const appHistory = useRouterHistory(createMemoryHistory)({
      queryKey: false,
      parseQueryString,
      stringifyQuery
    });

    const pushpinsControl = new PushpinsControl();
    pushpinsControl.setDispatch(this.dispatch);

    this.store = createStore({
      jsonApi: new JsonAPI(this.dispatch, this.getState),
      issuesV2Api: new IssuesV2Api(this.dispatch, this.getState),
      appHistory,
      pushpinsControl,
      analyticsManager
    });
  }

  componentDidMount() {
    const {env, region, hostingAppName} = this.props;
    this.propResolvers = {};
    const propPromises = {};
    Object.values(entityPropTypes).forEach(propName => {
      if (this.props[propName]) {
        propPromises[propName] = Promise.resolve(this.props[propName]);
      } else {
        propPromises[propName] = new Promise(resolve => {
          this.propResolvers[propName] = resolve;
        });
      }
    });

    const launchDarklyInitializedPromise = this.initializeFFs(
      propPromises[entityPropTypes.PROJECT_ENTITLEMENTS],
      propPromises[entityPropTypes.CURRENT_USER],
      region,
      hostingAppName,
      env
    );

    Promise.all([
      getCurrentToken(ENV_CONFIGS.DOCS),
      propPromises[entityPropTypes.PROJECT_ENTITLEMENTS],
      propPromises[entityPropTypes.CURRENT_USER],
      propPromises[entityPropTypes.ROLES],
      propPromises[entityPropTypes.USERS],
      propPromises[entityPropTypes.COMPANIES],
      launchDarklyInitializedPromise
    ]).then(([token, currentProjectEntitlements, currentUser, roles, users, companies]) => {
      this.initStore({token, currentProjectEntitlements, currentUser, roles, users, companies});
    });
  }

  componentDidUpdate() {
    Object.values(entityPropTypes).forEach(propName => {
      if (this.props[propName] && this.propResolvers[propName]) {
        this.propResolvers[propName](this.props[propName]);
        delete this.propResolvers[propName];
      }
    });
  }

  initializeFFs(projectEntitlementsPromise, currentUserPromise, region, hostingAppName, env) {
    return Promise.all([projectEntitlementsPromise, currentUserPromise])
      .then(([currentProjectEntitlements, currentUser]) => {
        const {id: projectId, accountId} = currentProjectEntitlements;
        const [major, minor, patch] = version
          .match(/^(\d+)\.(\d+)\.(\d+)[^\d]?.*$/)
          .slice(1)
          .map(x => Number(x));
        const launchDarklyUserObject = {custom: {region, accountId, projectId, version, major, minor, patch}};
        const analyticsId = currentUser.analyticsId;
        if (analyticsId) {
          launchDarklyUserObject.key = analyticsId;
        }
        if (hostingAppName) {
          launchDarklyUserObject.custom.hostingAppName = hostingAppName;
        }
        return launchDarklyUserObject;
      })
      .then(launchDarklyUserObject => initializeFF(env, region, launchDarklyUserObject))
      .then(() => {
        this.store.dispatch(setFFLoaded());
      });
  }

  dispatch(action) {
    return this.store.dispatch(action);
  }

  getState() {
    return this.store.getState();
  }

  initStore({token, currentProjectEntitlements, currentUser, users, roles, companies}) {
    const {region, hostingAppName, enablePushpinSnapshots} = this.props;

    const containerId = currentProjectEntitlements.containers.find(
      container => container.type === CONTAINER_TYPES.ISSUES
    ).id;
    const lbsContainerId = currentProjectEntitlements.containers.find(
      container => container.type === CONTAINER_TYPES.LBS
    ).id;
    const reportContainerId = currentProjectEntitlements.containers.find(
      container => container.type === CONTAINER_TYPES.REPORT
    ).id;

    const projectId = currentProjectEntitlements.id;
    const docsSDK = new DocsSDKWrapper(ENV_CONFIGS.DOCS_SDK_CONFIG, token);
    const apiConfig = {containerId, lbsContainerId, reportContainerId, projectId};
    const analyticsId = currentUser.analyticsId;

    // API configs
    this.store.dispatch(configureJsonApiDocsSDKAction(docsSDK));
    this.store.dispatch(configureJsonApiAction(apiConfig));
    this.store.dispatch(setOxygenEntitiesAction({users, roles, companies}));
    this.store.dispatch(setCurrentProject(currentProjectEntitlements));
    // Analytics data
    this.store.dispatch(configureAnalyticsAction({hostingAppName, analyticsId}));
    this.store.dispatch(setAnalyticsId(analyticsId));
    this.store.dispatch(
      registerAnalyticsMandatorySuperProps(
        registerAnalyticsSuperPropsHelper(currentProjectEntitlements, region, hostingAppName)
      )
    );
    this.store.dispatch(setAnalyticsMandatoryPeopleProps(getAnalyticsMandatoryPeopleProps(currentUser)));
    this.store.dispatch(setAnalyticsPeopleProps(getAnalyticsPeopleProps(currentUser)));

    // Fetch relevant data
    this.store.dispatch(fetchRequiredFields());
    this.store.dispatch(fetchCurrentUserAction());
    this.store.dispatch(fetchCurrentDocsProjectAction());
    this.store.dispatch(fetchIssuesCustomizationMetadata());
    this.store.dispatch(fetchIssueRootCausesAction());
    this.store.dispatch(
      setFeatureFlagAction({
        enablePushpinSnapshots
      })
    );

    // Fetch templates data
    this.store.dispatch(fetchIssueTemplateCategoriesAction());
    this.store.dispatch(fetchActiveTemplatesAction());
    LocationsThunk.getDefaultTree()(this.dispatch, this.getState);
  }

  getToken() {
    return getToken(this.store);
  }

  render() {
    const {children, locale, region, env, currentProjectEntitlements, currentUser} = this.props;
    return (
      <Provider store={this.store} context={STORE_CONTEXT}>
        <MatrixProvider
          locale={locale}
          env={ENV_TYPE_BY_ISSUES_ENV_TYPE[env]}
          region={region}
          project={currentProjectEntitlements}
          user={currentUser}
          getToken={this.getToken}
        >
          <DeletedUsersProvider projectId={currentProjectEntitlements.id}>{children}</DeletedUsersProvider>
        </MatrixProvider>
      </Provider>
    );
  }
}

IssueProvider.propTypes = {
  /**
   * The ENV - one of `ISSUES_ENV_TYPES` values
   */
  env: PropTypes.oneOf(Object.values(ISSUES_ENV_TYPES)).isRequired,
  /**
   * The Region - one of `ISSUES_REGIONS` values
   */
  region: PropTypes.oneOf(Object.values(ISSUES_REGIONS)).isRequired,
  /**
   * The locale to use.
   */
  locale: PropTypes.string.isRequired,
  /**
   * Current project entitlements. Should be in the shape of `@adsk/bim360-mc-interface/es/models/EntitlementProjectItem`
   */
  currentProjectEntitlements: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    accountId: PropTypes.string.isRequired,
    accountName: PropTypes.string.isRequired,
    projectScope: PropTypes.arrayOf(PropTypes.string).isRequired,
    containers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired
      })
    ).isRequired
  }),
  /**
   * The current user
   */
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
    analyticsId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired
  }),
  /**
   * Users in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/User`
   */
  users: PropTypes.arrayOf(oxygenUserType),
  /**
   * Roles in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Role`
   */
  roles: PropTypes.arrayOf(roleType),
  /**
   * Companies in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Company`
   */
  companies: PropTypes.arrayOf(companyType),
  /**
   * Optional parameter that will override the apigee server address of all apigee calls.
   */
  apigeeUrl: PropTypes.string,
  /**
   * Hosting app name - used for analytics and FF.
   */
  hostingAppName: PropTypes.string.isRequired,
  /**
   * A single node to render as a child
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether or not to enable pushpin snapshots (default is true)
   */
  enablePushpinSnapshots: PropTypes.bool
};

IssueProvider.defaultProps = {
  enablePushpinSnapshots: true
};

IssueProvider.displayName = 'IssueProvider';

export default IssueProvider;
